import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import {
  Alert,
  Platform,
  Pressable,
  View,
  FlatList,
  Image,
  ScrollView,
  Dimensions,
} from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Text, Title } from '@constants/Themed';
import apis from '@api/api';
import DropDownView from '@components/Global/DropDownView';
import PlanTypes from '../SubComponents/PlanTypes';
import * as Stripe from '@stripe/stripe-react-native';
import { createURL } from 'expo-linking';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import styles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import WebBilling from './WebBilling';
import { useDispatch } from 'react-redux';
import { Button, Card, H5, H6, Separator, YStack } from 'tamagui';
import LottieView from 'lottie-react-native';
import ErrorBoundary from '@utils/ErrorBoundary';
import { BillingInfo } from '../types/types';

/**
 * UpgradeComponent - Stripe-based subscription plans component
 * 
 * This component works on BOTH web and native platforms.
 * It handles Stripe subscription plans with planGroups, selectedGroup filtering,
 * and plan rendering logic that matches the web version functionality.
 * 
 * Note: This is NOT the IAP component. For IAP, see UpgradeComponent.native.tsx
 */
const customAppearance = {
  font: {
    family: Platform.OS === 'android' ? 'avenirnextregular' : 'AvenirNext-Regular',
  },
  shapes: {
    borderRadius: 12,
    borderWidth: 0.5,
  },
  primaryButton: {
    shapes: {
      borderRadius: 20,
    },
  },
  colors: {
    primary: '#fcfdff',
    background: '#ffffff',
    componentBackground: '#f3f8fa',
    componentBorder: '#f3f8fa',
    componentDivider: '#000000',
    primaryText: '#000000',
    secondaryText: '#000000',
    componentText: '#000000',
    placeholderText: '#73757b',
  },
};

interface UpgradeComponentProps {
  userPlan: any;
  selectedGroup?: string;
  onGroupChange?: (group: 'monthly' | 'annual') => void;
  planGroups?: PlanGroup[];
  planIsLoading?: boolean;
}

type UpgradeComponentHandle = {
  openPaymentModal: () => void;
};

// Types for better type safety
interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  default_price: {
    id: string;
    unit_amount: number | null;
    currency: string;
    recurring: {
      interval: 'month' | 'year';
      interval_count: number;
    } | null;
    type: 'recurring' | 'one_time';
    billing_scheme: 'per_unit' | 'tiered';
  };
  features: Array<{ name: string }>;
  marketing_features: Array<{ name: string }>;
  metadata: Record<string, string>;
  images: string[];
}

interface PlanGroup {
  title: string;
  plans: StripeProduct[];
}

const UpgradeComponent = forwardRef<UpgradeComponentHandle, UpgradeComponentProps>(({ userPlan, selectedGroup = 'monthly', onGroupChange, planGroups = [], planIsLoading = false }, ref) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const [selectedItem, setSelectedItem] = useState<StripeProduct | null>(null);
  const paymentRef = useRef(null);
  const queryClient = useQueryClient();
  const currentPlan: BillingInfo = queryClient.getQueryData(['subscriptionPlan']);

  // Calculate total plans for logging
  const totalPlans = useMemo(() => planGroups.reduce((sum, g) => sum + (g?.plans?.length || 0), 0), [planGroups]);

  console.log('💳 UpgradeComponent (Stripe) RENDERED:', {
    planGroupsLength: planGroups.length,
    totalPlans,
    selectedGroup,
    planIsLoading,
    planGroupsDetails: planGroups.map(g => ({
      title: g.title,
      plansCount: g?.plans?.length || 0,
      planNames: g?.plans?.map(p => p.name).slice(0, 3) || []
    }))
  });

  const { initPaymentSheet, presentPaymentSheet } =
    Platform.OS !== 'web' ? Stripe.useStripe() : { initPaymentSheet: null, presentPaymentSheet: null };


  // Utility functions
  const formatPrice = (amount: number | null, currency: string) => {
    if (amount === null || amount === undefined) {
      return 'Contact Sales';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getPlanFeatures = (product: StripeProduct) => {
    return product.marketing_features?.length > 0
      ? product.marketing_features
      : product.features || [];
  };

  const isCurrentPlan = (product: StripeProduct) => {
    return currentPlan?.product?.id === product.id;
  };

  const getSavingsPercentage = (monthlyPrice: number, annualPrice: number) => {
    if (!monthlyPrice || !annualPrice) return 0;
    const monthlyAnnual = monthlyPrice * 12;
    return Math.round(((monthlyAnnual - annualPrice) / monthlyAnnual) * 100);
  };

  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const openPaymentModal = useCallback(async () => {
    if (!selectedItem || paymentInProgress) return;

    if (Platform.OS === 'web') {
      // For web, use checkout session
      try {
        setPaymentInProgress(true);
        dispatch({ type: SHOW_LOADING_MODAL });

        if (!selectedItem.default_price?.id) {
          throw new Error('Price ID not found for this product');
        }

        // Get checkout session
        const checkoutSession = await apis.getCheckoutSession({
          productId: selectedItem.default_price.id,
        });

        if (checkoutSession?.clientSecret || checkoutSession?.url) {
          // For web, redirect to Stripe hosted checkout
          if (typeof window !== 'undefined' && window.location && checkoutSession.url) {
            window.location.href = checkoutSession.url;
          } else if (checkoutSession?.clientSecret) {
            // If we have clientSecret but no URL, we could use embedded checkout
            // For now, show an error asking user to try again
            throw new Error('Checkout session created but redirect URL not available. Please try again.');
          } else {
            throw new Error('Failed to create checkout session');
          }
        } else {
          throw new Error('Failed to create checkout session');
        }
      } catch (error) {
        console.error('Checkout error:', error);
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: error.message || 'Failed to start checkout. Please try again.',
            type: 'error',
          },
        });
        dispatch({ type: HIDE_LOADING_MODAL });
        setPaymentInProgress(false);
      }
    } else {
      // For native, use payment sheet (handled in native component)
      paymentRef.current?.openModal?.(selectedItem);
    }
  }, [selectedItem, paymentInProgress, dispatch]);

  const RenderSubscriptionItem = ({ item, index }: { item: StripeProduct; index: number }) => {
    const isSelected = selectedItem?.id === item.id;
    const isCurrent = isCurrentPlan(item);
    const features = getPlanFeatures(item);
    const price = formatPrice(item.default_price.unit_amount, item.default_price.currency);
    const interval = item.default_price.recurring?.interval === 'year' ? 'year' : 'month';
    const isTieredPricing = item.default_price.billing_scheme === 'tiered';

    // Debug card rendering
    console.log(`🔄 Rendering card ${index}:`, {
      itemName: item.name,
      isSelected,
      isCurrent
    });

    return (
      <Pressable
        disabled={isCurrent}
        onPress={() =>
          setSelectedItem((prev) => (prev?.id === item.id ? null : item))
        }
        style={Platform.OS === 'web'
          ? {
            ...styles.modernCard,
            ...(theme ? styles.modernBgLight : styles.modernBgDark),
            width: 300, // Fixed width for horizontal FlatList
            minHeight: 480,
            maxHeight: 600,
            flexShrink: 0,
            borderWidth: isSelected ? 2 : 1,
            borderColor: isSelected ? '#3b82f6' : isCurrent ? '#22c55e' : (theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'),
            backgroundColor: isSelected
              ? (theme ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.1)')
              : isCurrent
                ? (theme ? 'rgba(34, 197, 94, 0.05)' : 'rgba(34, 197, 94, 0.1)')
                : (theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)'),
            opacity: isCurrent ? 0.8 : 1
          }
          : [
            styles.modernCard,
            theme ? styles.modernBgLight : styles.modernBgDark,
            {
              width: 300, // Fixed width for horizontal FlatList - match native version
              minHeight: 480,
              maxHeight: 600,
              flexShrink: 0,
              borderWidth: isSelected ? 2 : 1,
              borderColor: isSelected ? '#3b82f6' : isCurrent ? '#22c55e' : (theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'),
              backgroundColor: isSelected
                ? (theme ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.1)')
                : isCurrent
                  ? (theme ? 'rgba(34, 197, 94, 0.05)' : 'rgba(34, 197, 94, 0.1)')
                  : (theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)'),
              opacity: isCurrent ? 0.8 : 1
            }
          ]
        }
      >
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          <View style={Platform.OS === 'web'
            ? { ...styles.modernColumn, flex: 1, padding: 20 }
            : [styles.modernColumn, { flex: 1, padding: 20 }]
          }>
            {/* Status Indicators */}
            {isCurrent && (
              <View style={Platform.OS === 'web'
                ? {
                  ...styles.modernInfoRow,
                  backgroundColor: theme ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.15)',
                  borderColor: theme ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.4)',
                  borderWidth: 1,
                  marginBottom: 16,
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                }
                : [
                  styles.modernInfoRow,
                  {
                    backgroundColor: theme ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.15)',
                    borderColor: theme ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.4)',
                    borderWidth: 1,
                    marginBottom: 16,
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                  }
                ]
              }>
                <Text style={Platform.OS === 'web'
                  ? { ...styles.modernFieldLabel, color: '#22c55e', textAlign: 'center', fontWeight: '700', fontSize: 12 }
                  : [styles.modernFieldLabel, { color: '#22c55e', textAlign: 'center', fontWeight: '700', fontSize: 12 }]
                }>
                  Your Current Plan
                </Text>
              </View>
            )}

            {/* Plan Image */}
            <View style={Platform.OS === 'web'
              ? { ...styles.modernSection, alignItems: 'center', marginBottom: 16 }
              : [styles.modernSection, { alignItems: 'center', marginBottom: 16 }]
            }>
              <View style={{
                width: 80,
                height: 80,
                borderRadius: 20,
                backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'rgba(248, 250, 252, 0.8)',
                borderWidth: 2,
                borderColor: isSelected ? '#3b82f6' : 'rgba(0, 0, 0, 0.1)',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                ...(Platform.OS === 'web' && {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                })
              }}>
                <Image source={{ uri: item.images[0] }} style={{ width: 80, height: 80 }} />
              </View>
            </View>

            {/* Plan Header */}
            <View style={Platform.OS === 'web'
              ? { ...styles.modernSection, alignItems: 'center', marginBottom: 20 }
              : [styles.modernSection, { alignItems: 'center', marginBottom: 20 }]
            }>
              <Text style={Platform.OS === 'web'
                ? {
                  ...styles.modernCardTitle,
                  fontSize: 22,
                  fontWeight: '700',
                  textAlign: 'center',
                  marginBottom: 16,
                  color: isSelected ? (theme ? '#1e293b' : '#f1f5f9') : (theme ? '#334155' : '#94a3b8'),
                  lineHeight: 28,
                  paddingHorizontal: 8
                }
                : [
                  styles.modernCardTitle,
                  {
                    fontSize: 22,
                    fontWeight: '700',
                    textAlign: 'center',
                    marginBottom: 16,
                    color: isSelected ? (theme ? '#1e293b' : '#f1f5f9') : (theme ? '#334155' : '#94a3b8'),
                    lineHeight: 28,
                    paddingHorizontal: 8
                  }
                ]
              }>
                {item.name}
              </Text>

              <View style={[styles.modernRow, {
                alignItems: 'baseline',
                marginBottom: 12,
                paddingHorizontal: 8,
                justifyContent: 'center'
              }]}>
                <Text style={[
                  styles.modernFieldValue,
                  {
                    fontSize: isTieredPricing ? 32 : 40,
                    fontWeight: '700',
                    color: isSelected ? '#3b82f6' : '#1e293b',
                    textAlign: 'center',
                    lineHeight: 48
                  }
                ]}>
                  {price}
                </Text>
                {!isTieredPricing && (
                  <Text style={[
                    styles.modernFieldValue,
                    {
                      fontSize: 20,
                      color: '#64748b',
                      marginLeft: 8,
                      lineHeight: 28
                    }
                  ]}>
                    /{interval}
                  </Text>
                )}
              </View>
              {isTieredPricing && (
                <Text style={[
                  styles.modernFieldValue,
                  {
                    fontSize: 16,
                    color: '#64748b',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    lineHeight: 22
                  }
                ]}>
                  Custom pricing available
                </Text>
              )}
            </View>

            {/* Description */}
            <View style={[styles.modernSection, { marginBottom: 20 }]}>
              <Text
                style={[
                  styles.modernFieldValue,
                  {
                    textAlign: 'center',
                    lineHeight: 22,
                    fontSize: 15,
                    color: '#64748b',
                    paddingHorizontal: 4,
                  }
                ]}
                numberOfLines={4}
                ellipsizeMode="tail"
              >
                {item.description}
              </Text>
            </View>

            {/* Features */}
            {features.length > 0 && (
              <View style={[styles.modernSection, { flex: 1, marginBottom: 20 }]}>
                <Text style={[
                  styles.modernFieldLabel,
                  {
                    textAlign: 'center',
                    marginBottom: 16,
                    fontSize: 16,
                    fontWeight: '600',
                    color: '#1e293b'
                  }
                ]}>
                  Features
                </Text>
                <ScrollView
                  style={{ maxHeight: 140 }}
                  showsVerticalScrollIndicator={false}
                >
                  {features.slice(0, 6).map((feature, idx) => (
                    <View key={idx} style={[styles.modernRow, {
                      marginBottom: 8,
                      alignItems: 'flex-start'
                    }]}>
                      <Text style={{
                        color: '#22c55e',
                        marginRight: 10,
                        fontSize: 16,
                        fontWeight: '600',
                        marginTop: 2
                      }}>
                        ✓
                      </Text>
                      <Text style={[
                        styles.modernFieldValue,
                        {
                          fontSize: 14,
                          lineHeight: 20,
                          flex: 1,
                          color: '#475569'
                        }
                      ]}>
                        {feature.name}
                      </Text>
                    </View>
                  ))}
                  {features.length > 6 && (
                    <Text style={[
                      styles.modernFieldValue,
                      {
                        fontSize: 12,
                        color: '#94a3b8',
                        textAlign: 'center',
                        fontStyle: 'italic',
                        marginTop: 8
                      }
                    ]}>
                      +{features.length - 6} more features
                    </Text>
                  )}
                </ScrollView>
              </View>
            )}

            {/* Selected Indicator */}
            {isSelected && (
              <View style={[
                styles.modernInfoRow,
                {
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                  borderWidth: 1,
                  marginTop: 8,
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                }
              ]}>
                <Text style={[
                  styles.modernFieldLabel,
                  {
                    color: '#3b82f6',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: 12
                  }
                ]}>
                  Selected Plan
                </Text>
              </View>
            )}
          </View>
        </ScrollView>
      </Pressable>
    );
  };

  // Get all plans for the selected group
  // Try to find matching group, fallback to first available group if no match
  const allPlans = useMemo(() => {
    console.log('🔍 Finding plans for selectedGroup:', selectedGroup, {
      planGroupsLength: planGroups.length,
      planGroups: planGroups.map(g => ({
        title: g.title,
        plansCount: g.plans.length,
        titleLower: g.title.toLowerCase()
      }))
    });

    const matchingGroup = planGroups.find(group =>
      group.title.toLowerCase().includes(selectedGroup)
    );

    if (matchingGroup?.plans?.length > 0) {
      console.log('✅ Found matching group:', matchingGroup.title, 'with', matchingGroup.plans.length, 'plans');
      console.log('✅ Plan names:', matchingGroup.plans.map(p => p.name));
      return matchingGroup.plans;
    }

    // Fallback: if no match, try to get any available plans
    if (planGroups.length > 0) {
      // Try monthly first, then annual, then any
      const monthlyGroup = planGroups.find(g => g.title.toLowerCase().includes('monthly'));
      const annualGroup = planGroups.find(g => g.title.toLowerCase().includes('annual'));
      const fallbackGroup = monthlyGroup || annualGroup || planGroups[0];

      if (fallbackGroup?.plans?.length > 0) {
        console.log('⚠️ No match for', selectedGroup, '- using fallback group:', fallbackGroup.title, 'with', fallbackGroup.plans.length, 'plans');
        return fallbackGroup.plans;
      }
    }

    console.log('❌ No plans found in any group. planGroups:', planGroups);
    return [];
  }, [planGroups, selectedGroup]);

  // Filter out free plan if user has a higher-tier plan
  const currentPlans = useMemo(() => {
    if (!currentPlan?.product?.name) return allPlans;

    const userPlanName = currentPlan.product.name.toLowerCase();
    const isOnFreePlan = userPlanName.includes('free') ||
      userPlanName.includes('starter') ||
      userPlanName.includes('exempt');

    // If user is on free plan, show all plans including free
    if (isOnFreePlan) return allPlans;

    // If user has a paid plan, hide free plans
    return allPlans.filter(plan => {
      const planName = plan.name.toLowerCase();
      return !planName.includes('free') &&
        !planName.includes('starter') &&
        !planName.includes('exempt');
    });
  }, [allPlans, currentPlan?.product?.name]);


  // Debug plan loading and layout
  React.useEffect(() => {
    console.log('🔄 UpgradeComponent plan data changed:', {
      planGroupsLength: planGroups.length,
      planGroupsDetails: planGroups.map(g => ({
        title: g.title,
        plansCount: g.plans.length,
        planNames: g.plans.map(p => p.name).slice(0, 3)
      })),
      allPlansLength: allPlans.length,
      currentPlansLength: currentPlans.length,
      selectedGroup,
      planIsLoading,
      hasCurrentPlan: !!currentPlan
    });
  }, [planGroups, allPlans, currentPlans, selectedGroup, planIsLoading, currentPlan]);

  return (
    <View style={Platform.OS === 'web'
      ? { ...styles.modernMainContent, flex: 1, padding: 0, width: '100%', overflow: 'hidden' }
      : [styles.modernMainContent, { flex: 1, padding: 0, width: '100%', overflow: 'hidden' }]
    }>
      <WebBilling ref={paymentRef} />


      {/* Plans Section */}
      <View style={Platform.OS === 'web'
        ? {
          ...styles.modernSection,
          flex: 1,
          paddingHorizontal: 0,
          paddingTop: 24,
          width: '100%',
          maxHeight: 700,
          overflow: 'hidden'
        }
        : [styles.modernSection, {
          flex: 1,
          paddingHorizontal: 0,
          paddingTop: 24,
          paddingBottom: 120,
          width: '100%',
          maxHeight: 700,
          overflow: 'hidden'
        }]
      }>
        {planIsLoading ? (
          <View style={[styles.modernAnimationContainer, { minHeight: 200 }]}>
            <LottieView
              source={require('@assets/animations/spin-leaf-loader.json')}
              autoPlay
              loop
              style={{ width: 100, height: 100 }}
            />
          </View>
        ) : currentPlans && currentPlans.length > 0 ? (
          (() => {
            console.log('✅ Rendering', currentPlans.length, 'plans for', selectedGroup);
            return (
              <View style={{
                flex: 1,
                width: '100%',
                minHeight: 500, // Ensure minimum height for visibility
                overflow: 'hidden'
              }}>
                <FlatList
                  data={currentPlans}
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  scrollEnabled={true}
                  bounces={false}
                  alwaysBounceHorizontal={false}
                  centerContent
                  contentContainerStyle={{
                    gap: 10,
                    paddingHorizontal: Platform.OS === 'web' ? '5%' : '10%',
                    paddingVertical: 20,
                    alignItems: 'center',
                    minHeight: 500 // Ensure content has minimum height
                  }}
                  style={{
                    flex: 1,
                    width: '100%',
                    ...(Platform.OS !== 'web' && { marginHorizontal: -20 })
                  }}
                  renderItem={({ item, index }) => (
                    <RenderSubscriptionItem item={item} index={index} />
                  )}
                  keyExtractor={(item) => item.id}
                />
              </View>
            );
          })()
        ) : (
          (() => {
            console.log('⚠️ No plans to render:', {
              planIsLoading,
              currentPlansLength: currentPlans?.length,
              allPlansLength: allPlans?.length,
              planGroupsLength: planGroups.length,
              selectedGroup,
              planGroups: planGroups.map(g => ({ title: g.title, count: g.plans.length }))
            });
            return (
              <View style={[styles.modernCard, styles.modernBgLight, {
                padding: 32,
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                alignSelf: 'center',
                maxWidth: 400
              }]}>
                <Text style={[styles.modernFieldValue, {
                  textAlign: 'center',
                  fontSize: 16,
                  fontWeight: '600',
                  marginBottom: 8
                }]}>
                  {planIsLoading ? 'Loading plans...' : `No ${selectedGroup} plans available`}
                </Text>
                <Text style={[styles.modernFieldValue, {
                  textAlign: 'center',
                  fontSize: 14,
                  color: '#64748b'
                }]}>
                  {planGroups.length === 0
                    ? 'Please check back later for available subscription options'
                    : `No ${selectedGroup} plans found. Try switching to ${selectedGroup === 'monthly' ? 'annual' : 'monthly'} plans.`
                  }
                </Text>
              </View>
            );
          })()
        )}
      </View>

      {/* Checkout Button */}
      {selectedItem && (
        <Animatable.View
          transition="flex"
          style={[
            {
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderTopWidth: 1,
              borderTopColor: 'rgba(0, 0, 0, 0.1)',
              paddingHorizontal: 24,
              paddingVertical: 20,
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(10px)',
                boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
              })
            },
          ]}
        >
          <View style={[styles.modernRow, {
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16
          }]}>
            <View style={{ flex: 1 }}>
              <Text style={[
                styles.modernFieldValue,
                {
                  fontSize: 18,
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: 4
                }
              ]}>
                {selectedItem.name}
              </Text>
              <Text style={[
                styles.modernFieldValue,
                {
                  fontSize: 14,
                  color: '#64748b'
                }
              ]}>
                {selectedItem.default_price.unit_amount ?
                  `${formatPrice(selectedItem.default_price.unit_amount, selectedItem.default_price.currency)}/${selectedItem.default_price.recurring?.interval}` :
                  'Custom Pricing'
                }
              </Text>
            </View>

            <Pressable
              onPress={() => {
                if (selectedItem && selectedItem.default_price) {
                  openPaymentModal()
                } else {
                  dispatch({
                    type: SHOW_AUTH_MODAL,
                    data: { content: "This plan may no longer be available, sorry for the inconvenience.", type: 'error' },
                  });
                }
              }}
              disabled={paymentInProgress}
              style={[
                {
                  backgroundColor: paymentInProgress ? '#94a3b8' : '#3b82f6',
                  paddingHorizontal: 32,
                  paddingVertical: 16,
                  borderRadius: 12,
                  minWidth: 140,
                  alignItems: 'center',
                  opacity: paymentInProgress ? 0.6 : 1,
                },
                Platform.OS === 'web' && {
                  cursor: paymentInProgress ? 'not-allowed' : 'pointer',
                } as any,
              ]}
            >
              <Text style={[
                styles.modernFieldValue,
                {
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 16,
                  letterSpacing: 0.5
                }
              ]}>
                {paymentInProgress ? 'Processing...' : 'Continue to Payment'}
              </Text>
            </Pressable>
          </View>

        </Animatable.View>
      )}
    </View>
  );
});

export default UpgradeComponent;
